import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie_consent_v1";
const CONSENT_VERSION = 1;

/**
 * Read saved consent, validates the version. Returns null if absent.
 */
export function getSavedConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Push the Google Consent Mode v2 state and persist user choice.
 */
function applyConsent(choices) {
  if (typeof window === "undefined") return;
  const payload = {
    ad_storage: choices.marketing ? "granted" : "denied",
    ad_user_data: choices.marketing ? "granted" : "denied",
    ad_personalization: choices.marketing ? "granted" : "denied",
    analytics_storage: choices.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  };
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", payload);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(["consent", "update", payload]);
  }
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ version: CONSENT_VERSION, choices, ts: Date.now() })
  );
  // Allow other components to react (e.g. lazy-init GA4)
  window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: choices }));
}

export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const saved = getSavedConsent();
    if (!saved) {
      // Slight delay to avoid LCP impact
      const timer = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for footer "Manage cookies" link
  useEffect(() => {
    const handler = () => {
      const saved = getSavedConsent();
      if (saved) {
        setAnalytics(!!saved.choices?.analytics);
        setMarketing(!!saved.choices?.marketing);
      }
      setShowPrefs(true);
      setOpen(true);
    };
    window.addEventListener("open-cookie-preferences", handler);
    return () => window.removeEventListener("open-cookie-preferences", handler);
  }, []);

  const acceptAll = () => {
    applyConsent({ analytics: true, marketing: true });
    setOpen(false);
    setShowPrefs(false);
  };

  const rejectAll = () => {
    applyConsent({ analytics: false, marketing: false });
    setOpen(false);
    setShowPrefs(false);
  };

  const saveChoices = () => {
    applyConsent({ analytics, marketing });
    setOpen(false);
    setShowPrefs(false);
  };

  if (!open) return null;

  // Customize mode = full modal-style; default = discreet bottom-right toast
  const isCompact = !showPrefs;

  return (
    <div
      className={
        isCompact
          ? "fixed bottom-4 right-4 left-4 sm:left-auto sm:right-6 sm:bottom-6 z-[60] animate-in slide-in-from-bottom duration-500 max-w-sm sm:max-w-md"
          : "fixed inset-x-0 bottom-0 z-[9998] px-4 pb-4 sm:px-6 sm:pb-6 animate-in slide-in-from-bottom duration-500"
      }
      role="dialog"
      aria-modal={isCompact ? "false" : "true"}
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-consent-banner"
    >
      <div
        className={
          isCompact
            ? "bg-[#001F3F]/95 backdrop-blur-md text-white border border-white/15 shadow-2xl rounded-sm overflow-hidden text-sm"
            : "mx-auto max-w-4xl bg-[#001F3F] text-white border border-white/15 shadow-2xl rounded-sm overflow-hidden"
        }
      >
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 ${isCompact ? "px-4 pt-4 pb-1" : "px-6 pt-6 pb-2"}`}>
          <div className="flex items-start gap-3">
            <Cookie size={isCompact ? 18 : 22} strokeWidth={1.5} className="text-[#FFD700] shrink-0 mt-0.5" />
            <div>
              <h2
                id="cookie-banner-title"
                className={
                  isCompact
                    ? "font-display text-sm font-bold uppercase tracking-tighter leading-tight"
                    : "font-display text-lg sm:text-xl font-bold uppercase tracking-tighter leading-tight"
                }
              >
                {t("cookies.title")}
              </h2>
            </div>
          </div>
          <button
            onClick={rejectAll}
            className="text-white/50 hover:text-white transition-colors"
            aria-label={t("cookies.close")}
            data-testid="cookie-banner-close"
          >
            <X size={isCompact ? 16 : 20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Body */}
        <div className={isCompact ? "px-4 pb-3" : "px-6 pb-5"}>
          <p className={`text-white/75 leading-relaxed ${isCompact ? "text-xs" : "text-sm"}`}>
            {isCompact
              ? "Nous utilisons des cookies pour mesurer le trafic et personnaliser les annonces."
              : t("cookies.description")}
            {!isCompact && " "}
            {!isCompact && (
              <Link
                to="/legal/cookies"
                className="underline hover:text-white"
                onClick={() => setOpen(false)}
                data-testid="cookie-banner-policy-link"
              >
                {t("cookies.learn_more")}
              </Link>
            )}
          </p>

          {/* Preferences panel */}
          {showPrefs && (
            <div className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="cookie-banner-prefs">
              <PrefRow
                title={t("cookies.cat_essential_title")}
                desc={t("cookies.cat_essential_desc")}
                locked
                checked
              />
              <PrefRow
                title={t("cookies.cat_analytics_title")}
                desc={t("cookies.cat_analytics_desc")}
                checked={analytics}
                onChange={setAnalytics}
                testid="cookie-pref-analytics"
              />
              <PrefRow
                title={t("cookies.cat_marketing_title")}
                desc={t("cookies.cat_marketing_desc")}
                checked={marketing}
                onChange={setMarketing}
                testid="cookie-pref-marketing"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className={`border-t border-white/10 bg-[#000B1A] flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end ${isCompact ? "px-4 py-3" : "px-6 py-4 sm:gap-3"}`}>
          {!showPrefs && (
            <button
              onClick={() => setShowPrefs(true)}
              className={`uppercase tracking-[0.2em] font-bold text-white/70 hover:text-white sm:mr-auto ${isCompact ? "text-[10px] py-1" : "text-xs"}`}
              data-testid="cookie-banner-customize"
            >
              {t("cookies.customize")}
            </button>
          )}
          <button
            onClick={rejectAll}
            className={`border border-white/30 text-white hover:bg-white/10 transition-all uppercase tracking-[0.2em] font-bold ${isCompact ? "text-[10px] px-4 py-2" : "text-xs px-6 py-3 tracking-[0.25em]"}`}
            data-testid="cookie-banner-reject"
          >
            {t("cookies.reject_all")}
          </button>
          {showPrefs ? (
            <button
              onClick={saveChoices}
              className="bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.25em] text-xs font-bold px-6 py-3"
              data-testid="cookie-banner-save"
            >
              {t("cookies.save_choices")}
            </button>
          ) : (
            <button
              onClick={acceptAll}
              className={`bg-white text-[#001F3F] hover:bg-[#FFD700] transition-all uppercase tracking-[0.2em] font-bold ${isCompact ? "text-[10px] px-4 py-2" : "text-xs px-6 py-3 tracking-[0.25em]"}`}
              data-testid="cookie-banner-accept"
            >
              {t("cookies.accept_all")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, locked, testid }) {
  return (
    <label className={`flex items-start gap-3 ${locked ? "opacity-70" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={!!checked}
        disabled={locked}
        onChange={(e) => onChange && onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-[#FFD700] shrink-0"
        data-testid={testid}
      />
      <div className="text-sm">
        <div className="font-semibold text-white">{title}</div>
        <div className="text-white/60 text-xs leading-relaxed mt-0.5">{desc}</div>
      </div>
    </label>
  );
}
